import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { fetchFeatureCatalog } from '../../services/api/adminFeatureCatalog';
import { grantOverride } from '../../services/api/adminFeatureOverrides';

const GrantOverrideModal = ({ open, workspaces, onClose, onGranted }) => {
  const [clientId, setClientId] = useState('');
  const [subFeatureId, setSubFeatureId] = useState('');
  const [state, setState] = useState('enabled');
  const [reason, setReason] = useState('');
  const [catalog, setCatalog] = useState({ features: [] });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    fetchFeatureCatalog().then((res) => setCatalog(res.data)).catch((e) => setError(e.message));
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError(null);
      await grantOverride({
        clientId: Number(clientId),
        subFeatureId: Number(subFeatureId),
        state,
        reason,
      });
      onGranted && onGranted();
      onClose();
      setClientId(''); setSubFeatureId(''); setReason('');
    } catch (err) {
      setError(err?.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-card rounded-lg w-full max-w-md p-4 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-foreground">Grant feature override</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-muted-foreground"><X className="h-5 w-5" /></button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3 text-sm">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Workspace</label>
            <select
              value={clientId} onChange={(e) => setClientId(e.target.value)} required
              className="w-full text-sm border border-input rounded-lg px-3 py-2"
            >
              <option value="">— Choose workspace —</option>
              {workspaces.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Feature</label>
            <select
              value={subFeatureId} onChange={(e) => setSubFeatureId(e.target.value)} required
              className="w-full text-sm border border-input rounded-lg px-3 py-2"
            >
              <option value="">— Choose feature —</option>
              {(catalog.features || []).flatMap((f) =>
                (f.sub_features || []).map((sf) => (
                  <option key={sf.id} value={sf.id}>{f.label} · {sf.label}</option>
                ))
              )}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">State</label>
            <select
              value={state} onChange={(e) => setState(e.target.value)}
              className="w-full text-sm border border-input rounded-lg px-3 py-2"
            >
              <option value="enabled">Enabled (grant)</option>
              <option value="disabled">Disabled (revoke)</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Reason</label>
            <input
              type="text" value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Beta access, escalation #123"
              className="w-full text-sm border border-input rounded-lg px-3 py-2"
            />
          </div>

          {error && <div className="p-2 bg-red-50 text-red-700 text-sm rounded">{error}</div>}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose}
                    className="px-4 py-2 text-sm text-foreground border border-input rounded-lg hover:bg-muted/50">
              Cancel
            </button>
            <button type="submit" disabled={saving}
                    className="px-4 py-2 text-sm font-medium text-white bg-black rounded-lg hover:bg-gray-800 disabled:opacity-50">
              {saving ? 'Granting...' : 'Grant override'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GrantOverrideModal;
